import "less/base.less";
import { createRoot } from "react-dom/client";
import * as React from "react";
import * as Sentry from "@sentry/react";
import {
  HflPostHogProvider,
  initializeExternalHflPostHogClient,
} from "@utdanningsdirektoratet/hflposthog";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
  createBrowserRouter,
} from "react-router-dom";
import { RouterProvider } from "react-router";
import {
  appEnvironment,
  institusjonNavnKort,
  postHog,
  sentry,
} from "./logic/initialState";
import App from "./App";
import StateProvider from "./StateProvider";
import Default from "./pages/default";
import Uttalelse from "./pages/uttalelse";
import { routes } from "./routes";
import Pdf from "./pages/uttalelse/Pdf";
import Personvern from "./pages/personvernserklæring";
import { isUdirTenant } from "./logic/tenantUtils";

Sentry.init({
  dsn: sentry,
  integrations: [
    Sentry.replayIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.thirdPartyErrorFilterIntegration({
      filterKeys: ["hflhss"],
      behaviour: "drop-error-if-contains-third-party-frames",
    }),
  ],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  tunnel: "/tunnel",
  release: `hoering@${process.env.PACKAGE_VERSION}`,
});

const router = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)([
  {
    path: "/",
    element: <App />,
    children: [
      {
        path: routes.pdfHoering(":hoeringId"),
        element: <Pdf />,
      },
      {
        path: routes.pdfUttalelse(":hoeringId", ":eksternId"),
        element: <Pdf type="uttalelse" />,
      },
      {
        path: routes.pdfUtkast(":hoeringId", ":eksternId"),
        element: <Pdf type="utkast" />,
      },
      ...[
        routes.hoering(":hoeringId"),
        routes.uttalelse(":hoeringId", ":eksternId"),
        routes.utkast(":hoeringId", ":eksternId"),
        routes.lesutkast(":hoeringId", ":leseTilgangsKode"),
      ].map((path) => ({
        path,
        element: <Uttalelse />,
      })),
      // Personvernserklaering route er kun relevant for Udir tenant
      ...(isUdirTenant
        ? [
            {
              path: routes.personvernerklaering(),
              element: <Personvern />,
            },
          ]
        : []),
      {
        path: "/cookies",
        element: <Default page="cookies" />,
      },
      {
        index: true,
        element: <Default />,
      },
      {
        path: "*",
        element: <Default page="notfound" />,
      },
    ],
  },
]);

const posthogClient = initializeExternalHflPostHogClient({
  environment: appEnvironment,
  vertikalName: "høring",
  postHogApiKey: postHog,
  debugInConsole: appEnvironment !== "prod",
  orgnummer: null,
  nonce: window.nonce,
  onInit: (posthog) => {
    posthog.group("tenant", institusjonNavnKort.toLowerCase(), {
      tenant: institusjonNavnKort.toLowerCase(),
    });
  },
});

const container = document.getElementById("app");
const root = createRoot(container!);
root.render(
  <React.StrictMode>
    <HflPostHogProvider client={posthogClient}>
      <Sentry.ErrorBoundary>
        <StateProvider>
          <RouterProvider router={router} />
        </StateProvider>
      </Sentry.ErrorBoundary>
    </HflPostHogProvider>
  </React.StrictMode>,
);
